import React, {createContext, useContext, useReducer} from 'react';

export interface SearchState {
  storeId: string | null;
  query: string;
  departmentId: string | null;
  sortOptionId: string;
  activeFacets: Record<string, string[]>;
  specials: boolean;
  /** DIS-2053: the page itself is a specials list, so a filter change cannot turn `specials` off. */
  specialsPinned: boolean;
  priceRange: {min: number; max: number} | null;
  isSearchBarExpanded: boolean;
}

export type SearchAction =
  | {type: 'SET_QUERY'; query: string}
  | {type: 'SET_DEPARTMENT'; departmentId: string | null}
  | {type: 'SET_SORT'; sortOptionId: string}
  | {type: 'TOGGLE_FACET'; group: string; value: string}
  | {type: 'SET_SPECIALS'; enabled: boolean; pinned?: boolean}
  | {type: 'SET_PRICE_RANGE'; min: number; max: number}
  | {type: 'CLEAR_PRICE_RANGE'}
  | {type: 'CLEAR_FILTERS'}
  | {type: 'CLEAR_ALL'}
  | {type: 'SET_SEARCH_BAR_EXPANDED'; expanded: boolean}
  | {type: 'SCOPE_TO_DEPARTMENT'; departmentId: string; sortOptionId?: string; specials?: boolean}
  | {type: 'SET_STORE_ID'; storeId: string}
  | {
      type: 'SET_FILTERS';
      facets: Record<string, string[]>;
      specials: boolean;
      priceRange: {min: number; max: number} | null;
    };

const initialState: SearchState = {
  storeId: null,
  query: '',
  departmentId: null,
  sortOptionId: 'top_products',
  activeFacets: {},
  specials: false,
  specialsPinned: false,
  priceRange: null,
  isSearchBarExpanded: false,
};

function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'SET_QUERY':
      return {...state, query: action.query};
    case 'SET_DEPARTMENT':
      return {...state, departmentId: action.departmentId};
    case 'SET_SORT':
      return {...state, sortOptionId: action.sortOptionId};
    case 'TOGGLE_FACET': {
      const existing = state.activeFacets[action.group] ?? [];
      const idx = existing.indexOf(action.value);
      const updated = idx >= 0 ? existing.filter((_, i) => i !== idx) : [...existing, action.value];
      const next = {...state.activeFacets};
      if (updated.length === 0) {
        delete next[action.group];
      } else {
        next[action.group] = updated;
      }
      return {...state, activeFacets: next};
    }
    case 'SET_SPECIALS':
      return {
        ...state,
        specials: action.enabled,
        specialsPinned: action.pinned ?? state.specialsPinned,
      };
    case 'SET_PRICE_RANGE':
      return {...state, priceRange: {min: action.min, max: action.max}};
    case 'CLEAR_PRICE_RANGE':
      return {...state, priceRange: null};
    case 'SET_FILTERS':
      return {
        ...state,
        activeFacets: action.facets,
        specials: state.specialsPinned || action.specials,
        priceRange: action.priceRange,
      };
    case 'CLEAR_FILTERS':
      return {
        ...state,
        sortOptionId: 'top_products',
        activeFacets: {},
        specials: state.specialsPinned,
        priceRange: null,
      };
    case 'CLEAR_ALL':
      return {...initialState, storeId: state.storeId};
    case 'SET_SEARCH_BAR_EXPANDED':
      return {...state, isSearchBarExpanded: action.expanded};
    case 'SET_STORE_ID':
      return {...initialState, storeId: action.storeId};
    case 'SCOPE_TO_DEPARTMENT':
      return {
        ...initialState,
        storeId: state.storeId,
        departmentId: action.departmentId || null,
        sortOptionId: action.sortOptionId ?? 'top_products',
        specials: action.specials ?? false,
        specialsPinned: action.specials ?? false,
      };
    default:
      return state;
  }
}

const SearchStateContext = createContext<SearchState | null>(null);
const SearchDispatchContext = createContext<React.Dispatch<SearchAction> | null>(null);

export function SearchProvider({children}: {children: React.ReactNode}) {
  const [state, dispatch] = useReducer(searchReducer, initialState);
  return (
    <SearchStateContext.Provider value={state}>
      <SearchDispatchContext.Provider value={dispatch}>{children}</SearchDispatchContext.Provider>
    </SearchStateContext.Provider>
  );
}

export function useSearchState(): SearchState {
  const state = useContext(SearchStateContext);
  if (state === null) {
    throw new Error('useSearchState must be used within SearchProvider');
  }
  return state;
}

export function useSearchDispatch(): React.Dispatch<SearchAction> {
  const dispatch = useContext(SearchDispatchContext);
  if (dispatch === null) {
    throw new Error('useSearchDispatch must be used within SearchProvider');
  }
  return dispatch;
}

/** Returns null outside SearchProvider (root-flow headers) instead of throwing. */
export function useOptionalSearchDispatch(): React.Dispatch<SearchAction> | null {
  return useContext(SearchDispatchContext);
}

/** @deprecated Use useSearchState() and useSearchDispatch() separately for better performance */
export function useSearchContext() {
  return {state: useSearchState(), dispatch: useSearchDispatch()};
}
